'use client';

import { SdxInputCustomEvent } from '@swisscom/sdx';
import { HTMLAttributes, InputEvent, useEffect, useRef } from 'react';

type OptionalProps = {
  type?: 'textarea' | 'text' | 'date' | 'datetime' | 'search';
  disabled?: boolean;
  inputMode?: 'decimal';
  label?: string;
  maxlength?: number;
  name?: string;
  placeholder?: string;
  readonly?: boolean;
  selectTextOnFocus?: boolean;
  srHint?: string;
  style?: HTMLAttributes<HTMLElement>['style'];
  value?: string;
  invalid?: boolean;
  error?: string;
  minDate?: string;
  onChange?: (value: string | null) => void;
  onBlur?: (value: string | null) => void;
  onSearch?: (value: string | null) => void;
};

type InputProps = OptionalProps;

const Input: React.FC<InputProps> = ({
  disabled,
  type,
  inputMode,
  label,
  maxlength,
  name,
  placeholder,
  readonly,
  selectTextOnFocus,
  srHint,
  value,
  minDate,
  onChange,
  onBlur,
  onSearch,
  invalid,
  error,
  style,
}) => {
  const ref = useRef<HTMLSdxInputElement>(null);


  useEffect(() => {
    const current = ref.current;

    if (current && type === 'search') {
      const handleSearch = (e: SdxInputCustomEvent<unknown>): void => {
        if (disabled) return;
        onSearch?.(e.target?.value ?? null);
      };

      current.addEventListener('sdxsearch', handleSearch);

      return () => {
        current.removeEventListener('sdxsearch', handleSearch);
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, onSearch, type]);

  const isDateType = type === 'date' || type === 'datetime';

  return (
    <sdx-input
      disabled={isDateType ? undefined : disabled}
      type={type === 'datetime' ? 'date' : type}
      inputMode={inputMode}
      label={label}
      maxlength={maxlength}
      valid={typeof invalid === 'undefined' ? undefined : !invalid}
      validation-message={invalid ? error : undefined}
      name={name}
      style={isDateType && disabled ? { ...style, pointerEvents: 'none', opacity: 0.5 } : style}
      flatpickrOptions={
        isDateType
          ? {
              ...(type === 'datetime' && {
                altInput: true,
                dateFormat: 'Z',
                altFormat: 'd.m.Y H:i',
                enableTime: true,
              }),
              ...(minDate && { minDate }),
            }
          : {}
      }
      placeholder={placeholder}
      selectTextOnFocus={selectTextOnFocus}
      srHint={srHint}
      value={value}
      ref={ref}
      onBlur={(e: {
        target?: { value?: string };
        stopPropagation: () => void;
      }) => {
        if (type === 'datetime') e.stopPropagation();
        else onBlur?.(e.target?.value ?? null);
      }}
      onInput={(e: InputEvent) => {
        if (disabled) return;
        if (type === 'datetime') e.stopPropagation();
        onChange?.((e.target as HTMLSdxInputElement)?.value ?? null);
      }}
    />
  );
};

export default Input;
export type { InputProps };
